<template>
  <div
    v-click-outside="vsoConfig"
    class="f-dropdown-overlay"
    :class="{reverse, variable}"
    @mousedown="noPropagation ? closeDropdown($event) : hideDropdown($event)"
  >
    <div
      class="wrapper"
      :style="dropdownDisplay"
      :class="{reverse}"
    >
      <FInpput
        v-if="!noSearch"
        ref="search"
        v-debounce:300="
          (val) => {
            slugList = [];
            search = val;
            $emit('filter', val);
          }
        "
        class="clickable f-head"
        icon="search"
        :value-in="search"
        :class="{
          small,
          bordered: minSearchLength && minSearchLength > search.length,
          reverse,
        }"
        :max-height="styleSet ? styleSet.hh : dropdownStyle.hh"
        :placeholder="placeholder || 'Rechercher ici ...'"
        clear
        no-prompt
        @clear="search = ''"
      />
      <div
        v-if="!minSearchLength || minSearchLength <= search.length"
        :style="
          noSearch
            ? 'height: 100%'
            : `height: calc(100%  - ${styleSet && Object.keys(styleSet).length ? styleSet.hh + 6 : dropdownStyle.hh + 6}px)`
        "
        :class="[
          'f-dropdown',
          (dropdownStyle && dropdownStyle.left) || (styleSet && styleSet.left)
            ? 'left-border-radius'
            : '',
          reverse ? 'reverse' : '',
        ]"
      >
        <slot
          v-if="emptyPlaceholder && !list.length"
          name="empty"
        />
        <slot v-if="!showSelected" />
        <div
          v-if="multiselect && showSelected"
          class="text-blue f-dropdown-adder"
        >
          <div class="text-gray translate">Eléments sélectionnés</div>
          <a
            class="clickable text-blue-slim translate"
            @click.stop="
              showSelected = false;
              staySelected = selected;
            "
          >
            Retour
          </a>
        </div>
        <div
          v-if="!noStickyHead"
          class="f-slug"
          :class="
            elementToDisplay && elementToDisplay.length > 0 ? 'slug-shown' : ''
          "
        >
          <transition-group name="list">
            <div
              v-for="(el, i) in elementToDisplay"
              :key="`fsn-${i}`"
              class="f-slug-name"
              :class="{translate: translate}"
            >
              {{ el }}
            </div>
          </transition-group>
        </div>
        <div
          v-else-if="list && list.list && (!maxLevel || maxLevel > 1)"
          class="f-slug"
        />
        <div
          ref="holder"
          class="f-elements-holder"
          :class="{
            'shallow-top':
              !noStickyHead ||
              (list && list.list && (!maxLevel || maxLevel > 1)),
          }"
        >
          <RecursiveList
            :deepest="deepest"
            :disable-adding="disableAdding"
            :filter="!ignoreFilter ? search : ''"
            :filter-selected="showSelected"
            :forecast-enabled-only="forecastEnabledOnly"
            :list="list"
            :max-level="maxLevel"
            :max-select-level="maxSelectLevel"
            :multiselect="multiselect"
            :only-alterable="onlyAlterable"
            :selected="selected"
            :stay-selected="staySelected"
            :select-deepest="selectDeepest"
            :slug-list="slugList"
            :variable="variable"
            :translate="translate"
            @add-element="$emit('addElement', $event)"
            @hidden="elementToDisplay = $event"
            @icon="$emit('icon', $event)"
            @select="
              multiselect ? setSelected($event) : $emit('select', $event)
            "
            @custom-select="
              multiselect ? setSelected($event) : $emit('customSelect', $event)
            "
            @slug="slugList.push($event)"
          />
        </div>
        <div
          v-if="!multiselect && !disableAdding"
          class="text-blue f-dropdown-adder"
        >
          <a
            class="clickable"
            @click.stop="$emit('addElement', [])"
          >
            <FontAwesomeIcon icon="plus" />
            <span
              v-if="adder"
              class="translate"
            >
              {{ adder }}
            </span>
            <span
              v-else
              class="translate"
              >Ajouter une catégorie</span
            >
          </a>
        </div>
        <slot
          v-if="!showSelected"
          name="bottom"
        />
        <div
          v-if="
            multiselect &&
            !showSelected &&
            ((emptyPlaceholder && list.length) || !emptyPlaceholder) &&
            !noButtons
          "
          class="text-blue f-dropdown-adder vertical"
          :class="{compact: compactButton}"
        >
          <a
            v-if="selected.length && !noSelectedList"
            class="clickable selected text-blue-slim translate"
            @click.stop="showSelected = true"
          >
            Eléments sélectionnés
          </a>
          <span
            v-else-if="!noSelectedList"
            style="height: 19px; width: 10px"
          />
          <a
            v-if="compactButton"
            class="clickable text-blue small-valid translate"
            @click="
              $emit('selected', selected);
              sthChanged = false;
            "
          >
            Valider
          </a>
          <FygrButton
            v-else
            class="f-dd-confirm translate"
            color="green"
            :disabled="(!selected.length && !sthChanged) || !sthChanged"
            @click="
              $emit('selected', selected);
              sthChanged = false;
            "
          >
            Valider
          </FygrButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapState} from 'vuex';
import vClickOutside from 'click-outside-vue3';
import {nextTick} from 'vue';
import vueDebounce from 'vue-debounce';
import RecursiveList from '@/components/elements/RecursiveList.vue';
import FInpput from '@/components/elements/FygrInput.vue'; // force import to use focus on refs

/**
 * CategoriesDropdown properties.
 * @prop {String} Adder - Text for adder line.
 * @prop {Boolean} disableAdding - Disable possibility to add new sub-entry.
 * @prop {Object} dropdownStyle - Dropdown position (required, check InvoiceWrapper to find
      how it looks).
 * @prop {Array} list - List of entries (required).
 * @prop {Number} maxLevel - Max level of depth in list.
 * @prop {Number} maxSelectLevel - Max level that possible to select.
 * @prop {Boolean} multiselect - Enable selecting multiple entries.
 * @prop {Boolean} noPropagation - Stop 'close' event propagation.
 * @prop {String} placeholder - Custom placeholder for input.
 * @prop {Array} preselected - List of preselected entries (temporary not supported).
 * @prop {Boolean} selectDeepest - Enables selecting only deepest entries level (maxLevel required).
 * @prop {Boolean} small - Change size of header.
 */
export default {
  name: 'CategoriesDropdownRectangle',
  components: {
    RecursiveList,
    FInpput,
  },
  directives: {
    clickOutside: vClickOutside.directive,
    debounce: vueDebounce({lock: true}),
  },
  props: {
    adder: {
      type: String,
      default: '',
    },
    ignoreFilter: Boolean,
    compactButton: Boolean,
    deepest: Boolean,
    disableAdding: Boolean,
    dropdownStyle: {
      type: Object,
      default: () => ({
        'max-height': 300,
        height: 300,
        hh: 40,
      }),
    },
    emptyPlaceholder: Boolean,
    forecastEnabledOnly: Boolean,
    list: {
      type: Array,
      default: () => [],
    },
    maxLevel: {
      type: Number,
      default: null,
    },
    maxSelectLevel: {
      type: Number,
      default: null,
    },
    minSearchLength: {
      type: Number,
      default: null,
    },
    multiselect: Boolean,
    noButtons: Boolean,
    noSelectedList: Boolean,
    noStickyHead: Boolean,
    noPropagation: Boolean,
    onlyAlterable: Boolean,
    placeholder: {
      type: String,
      default: '',
    },
    presearch: {
      type: String,
      default: '',
    },
    preselected: {
      type: Array,
      default: () => [],
    },
    eventContainer: {
      type: Object,
      default: () => {},
    },
    selectDeepest: Boolean,
    emitChanges: Boolean,
    small: Boolean,
    variable: Boolean,
    translate: {
      type: Boolean,
      default: false,
    },
    noSearch: {
      type: Boolean,
      default: false,
    },
    align: {
      type: String,
      default: 'right',
    },
  },
  emits: [
    'filter',
    'addElement',
    'icon',
    'select',
    'customSelect',
    'addElement',
    'close',
    'selected',
    'updateSelected',
  ],
  data() {
    return {
      styleSet: {},
      blockAssign: false,
      elementToDisplay: [],
      search: '',
      staySelected: [],
      selected: [],
      slugList: [],
      showSelected: false,
      sthChanged: false,
      vsoConfig: {
        handler: () => this.$emit('close'),
        events: ['mousedown'],
      },
    };
  },
  computed: {
    ...mapState('app', ['categories', 'scaleRatio']),
    reverse() {
      const source = Object.keys(this.styleSet).length
        ? this.styleSet
        : this.dropdownStyle;
      const height = source['max-height'] || source.height;
      return (
        source.bottom && !(source.bottom - height - 12 / this.scaleRatio > 0)
      );
    },
    //
    // IMPORTANT! to achieve correct positioning of dropdown (especially when we use it like wrapper) we need to adjust 'max-height' property in
    // component that uses this dropdown, otherwise calculation will be provided based on default 260px height. Example - if DD is used
    // as wrapper with <slot> and you want to have 300px of its height you need use  style: {...  'max-height': 300 ...}
    //
    dropdownDisplay() {
      const source = Object.keys(this.styleSet).length
        ? this.styleSet
        : this.dropdownStyle;
      const dict = {
        top: `${source.top + 5}px`,
        width: `${source.width}px`,
        'min-width': `${source['min-dd-width'] || source['min-width']}px`,
        'max-width': `${source['max-width']}px`,
        'max-height': `${source['max-height']}px`,
        height: `${source.height}px`,
        'margin-top': `${source['margin-top']}px`,
      };
      if (this.align === 'left') {
        dict.left = `${source.left}px`;
      } else {
        dict.right = `${source.right}px`;
      }
      if (this.reverse) {
        dict.top = 'unset';
        const height = source['max-height'] || source.height;
        const shift =
          source.innerHeight -
          75 / this.scaleRatio -
          source.bottom -
          height -
          12 / this.scaleRatio -
          source.targetHeight -
          5;
        if (shift < 0) {
          dict.bottom = `${source.bottom + source.targetHeight + shift}px`;
        } else {
          dict.bottom = `${source.bottom + 5 + source.targetHeight}px`;
        }
      }
      return dict;
    },
  },
  watch: {
    list: {
      handler() {
        this.slugList = [];
      },
      deep: true,
    },
    preselected: {
      handler() {
        this.sthChanged = true;
        this.selected = [...this.preselected];
        this.staySelected = [...this.preselected];
      },
      deep: true,
    },
    search() {
      this.blockAssign = true;
      setTimeout(() => {
        this.slugList = [];
        this.elementToDisplay = [];
        this.blockAssign = false;
      }, 100);
    },
    selected: {
      handler() {
        if (
          JSON.stringify(this.selected) !== JSON.stringify(this.preselected)
        ) {
          this.sthChanged = true;
        }
      },
      deep: true,
    },
    elementToDisplay() {
      if (
        this.blockAssign &&
        this.elementToDisplay &&
        this.elementToDisplay.length
      ) {
        this.elementToDisplay = [];
      }
    },
    eventContainer: {
      deep: true,
      handler(newValue) {
        if (newValue && Object.keys(newValue).length) {
          this.setStyle(newValue);
        }
      },
    },
  },

  mounted() {
    nextTick(() => {
      this.$refs?.search?.focus();
      if (this.eventContainer && Object.keys(this.eventContainer).length) {
        this.setStyle(this.eventContainer);
      }
    });
    this.search = this.presearch ? this.presearch : '';
    if (this.preselected) {
      this.selected = [...this.preselected];
      this.staySelected = [...this.preselected];
    }
  },
  methods: {
    setStyle({e, container, style}) {
      const rect = e.target.closest(container).getBoundingClientRect();
      this.styleSet = {
        top: rect.bottom,
        right: e.view.innerWidth - rect.right,
        left: rect.left,
        width: rect.width - 10,
        bottom: e.view.innerHeight - rect.bottom,
        height: 340 / this.scaleRatio,
        hh: 35 / this.scaleRatio,
        targetHeight: rect.height,
        innerHeight: e.view.innerHeight,
        ...style,
      };
    },
    hideDropdown(e) {
      if (e.target.closest('.f-head') || e.target.closest('.f-dropdown')) {
        return;
      }
      this.$emit('close');
    },
    closeDropdown(e) {
      e.stopPropagation();
      this.$emit('close');
    },
    setValue(val) {
      this.$emit('selected', val);
    },
    setSelected(id) {
      if (this.selected.includes(id)) {
        const idx = this.selected.findIndex((i) => i === id);
        this.selected.splice(idx, 1);
        this.staySelected.splice(idx, 1);
      } else {
        this.selected.push(id);
        this.staySelected.push(id);
      }
      this.$emit('updateSelected', this.selected, id);
      if (this.emitChanges) {
        this.$emit('select', id);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@use 'sass:color';
.f-dropdown-overlay.variable {
  position: fixed;
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  bottom: 0;
  background-color: rgba(rgba(0, 0, 0, 0), $alpha: 0);
  .wrapper {
    transform-origin: top;
    position: absolute;
    background: white;
    padding: var(--px5);
    border-radius: var(--px10);
    display: flex;
    flex-direction: column;
    border: 1px solid $gray;
    box-shadow: var(--px5) calc(var(--px2) * -1) var(--px25)
      calc(var(--px8) * -1) rgba(0, 0, 0, 0.45);
    &.reverse {
      flex-direction: column-reverse;
    }
    .f-head {
      background: $bg;
      border-radius: var(--px8);
      z-index: 100;
      &.reverse {
        padding-top: var(--px1);
      }
      &.bordered {
        border-radius: var(--px8);
      }
      &.small {
        height: var(--px30);
        width: var(--px60);
        margin-top: var(--px11);
        border-radius: var(--px5) var(--px5) 0 0;
      }
      :deep(.prefixed-input) {
        max-width: calc(100% - var(--px2));
        width: 100%;
        svg {
          font-size: var(--px14);
          top: calc(50% - var(--px7));
          margin-top: calc(var(--px1) * -1);
          &:first-child {
            left: var(--px15);
          }
          &:last-child {
            right: var(--px15);
          }
        }
        input {
          margin-left: calc(var(--px1) * -1);
          max-height: var(--px38);
          min-height: var(--px38);
          font-size: var(--px14);
          padding: 0 var(--px45);
          width: calc(100% - var(--px90));
        }
      }
      svg {
        margin-left: auto;
        color: $text-color-gray;
      }
      :deep(.prefixed-input) {
        z-index: 101;
      }
    }
    .f-dropdown {
      margin-top: var(--px5);
      margin-bottom: 0;
      z-index: 100;
      display: flex;
      flex-direction: column;
      top: var(--px211);
      right: var(--px38);
      background-color: white;
      border-radius: var(--px8) 0 var(--px8) var(--px8);
      &.reverse {
        border-radius: var(--px8) var(--px8) 0 var(--px8);
        margin-top: 0;
        margin-bottom: var(--px2);
      }
      &.left-border-radius {
        border-radius: 0 var(--px8) var(--px8) var(--px8);
      }
      .f-dropdown-adder {
        display: flex;
        height: var(--px50);
        padding: 0 var(--px20);
        align-items: center;
        justify-content: space-between;
        font-size: var(--px14);
        &.vertical {
          flex-direction: column;
          align-items: flex-start;
          margin-top: var(--px10);
          height: var(--px100);
          &.compact {
            height: var(--px38);
          }
          .f-dd-confirm {
            margin: var(--px20) auto;
            height: var(--px40);
            width: var(--px150);
          }
        }
        .small-valid {
          margin-left: auto;
          display: flex;
          font-weight: 600;
          flex: 0;
        }
        svg {
          margin-right: var(--px10);
        }
        .selected {
          font-weight: normal;
        }
      }
      .f-slug {
        display: flex;
        align-items: center;
        justify-content: space-between;
        height: auto;
        width: calc(100% - var(--px8));
        background-color: unset;
        gap: var(--px3);
        .f-slug-name {
          width: 100%;
          display: flex;
          word-wrap: break-word;
          overflow-wrap: break-word;
          font-size: var(--px12);
          color: $text-color-gray;

          &:nth-child(2) {
            justify-content: center;
          }
          &:last-child {
            justify-content: end;
            margin-right: var(--px5);
          }
          &:first-child {
            justify-content: start;
            margin-left: var(--px5);
          }
        }
      }
      .slug-shown {
        background-color: white;
        padding-bottom: var(--px3);
        border-bottom: 1px solid $bg;
      }
      .shallow-top {
        margin-top: 0;
      }
      .f-elements-holder {
        width: 100%;
        height: 100%;
        overflow: auto;
        &.small {
          max-height: var(--px210);
        }
        .f-element {
          height: var(--px40);
          display: flex;
          width: calc(100% - var(--px5));
          justify-content: center;
          align-items: flex-end;
          border-bottom: var(--px1) solid $bg;
          padding: 0 var(--px5);
          flex-direction: column;
          &:hover {
            background-color: $blue;
            color: white;
          }
        }
      }
    }
  }
}
.f-dropdown-overlay {
  position: fixed;
  z-index: 99;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  bottom: 0;
  background-color: rgba(rgba(0, 0, 0, 0), $alpha: 0);
  .wrapper {
    position: absolute;
    background: white;
    padding: var(--px5);
    border-radius: var(--px10);
    display: flex;
    flex-direction: column;
    border: 1px solid $gray;
    box-shadow: var(--px2) calc(var(--px2) * -1) var(--px20)
      calc(var(--px5) * -1) rgba(0, 0, 0, 0.35);
    .devider {
      width: 100%;
      background: $light-gray;
      height: 1px;
      margin: var(--px8) 0;
    }
    &.reverse {
      flex-direction: column-reverse;
      &::after {
        top: 100%;
        transform: translateX(-50%);
      }
      &::before {
        top: 100%;
        transform: translateX(-50%);
      }
    }
    .f-head {
      background: $bg;
      align-items: center;
      border-radius: var(--px8);
      z-index: 100;
      &.reverse {
        padding-top: var(--px1);
      }
      &.bordered {
        border-radius: var(--px8);
      }
      &.small {
        height: var(--px30);
        width: var(--px60);
        margin-top: var(--px11);
        border-radius: var(--px5) var(--px5) 0 0;
      }
      :deep(.prefixed-input) {
        max-width: calc(100% - var(--px2));
        width: 100%;
        svg {
          margin-top: calc(var(--px1) * -1);
        }
        input {
          max-height: var(--px37);
          min-height: var(--px37);
        }
      }
      svg {
        margin-left: auto;
        color: $text-color-gray;
      }
      :deep(.prefixed-input) {
        z-index: 101;
      }
    }
    .f-dropdown {
      margin-top: var(--px5);
      margin-bottom: 0;
      z-index: 100;
      display: flex;
      flex-direction: column;
      top: var(--px211);
      right: var(--px38);
      background-color: white;
      border-radius: var(--px8) 0 var(--px8) var(--px8);

      &.reverse {
        border-radius: var(--px8) var(--px8) 0 var(--px8);
        margin-top: 0;
        margin-bottom: var(--px5);
      }
      &.left-border-radius {
        border-radius: 0 var(--px8) var(--px8) var(--px8);
      }
      .f-dropdown-adder {
        display: flex;
        height: var(--px50);
        padding: 0 var(--px20);
        align-items: center;
        justify-content: space-between;
        &.vertical {
          flex-direction: column;
          align-items: flex-start;
          margin-top: var(--px10);
          height: var(--px100);
          &.compact {
            height: var(--px38);
          }
          .f-dd-confirm {
            margin: var(--px20) auto;
            height: var(--px40);
            width: var(--px150);
          }
        }
        .small-valid {
          margin-left: auto;
          display: flex;
          font-weight: 600;
          flex: 0;
        }
        svg {
          margin-right: var(--px10);
        }
        .selected {
          font-weight: normal;
        }
      }
      .f-slug {
        display: flex;
        align-items: center;
        justify-content: space-between;
        top: var(--px10);
        height: var(--px40);
        width: calc(100% - var(--px10));
        background-color: white;
        .f-slug-name {
          display: flex;
          word-wrap: break-word;
          overflow-wrap: break-word;
          font-size: var(--px12);
          color: $text-color-gray;
          &:first-child {
            margin-left: var(--px20);
          }
          &:last-child {
            margin-right: var(--px20);
          }
        }
      }
      .f-elements-holder {
        width: 100%;
        height: 100%;
        overflow: auto;
        &.small {
          max-height: var(--px210);
        }
        .f-element {
          height: var(--px40);
          display: flex;
          width: calc(100% - var(--px50));
          justify-content: center;
          align-items: flex-end;
          border-bottom: 1px solid $bg;
          padding: 0 var(--px5);
          flex-direction: column;
          &:hover {
            background-color: $blue;
            color: white;
          }
        }
      }
    }
  }
}
.list-enter-active,
.list-leave-active {
  transition: all 0.2s ease;
}
.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateX(2px);
}
</style>
